import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CustomField, IntakeFormSection, IntakeOption } from '@ee/common/models';
import {
  EVICTION_REASONS_CONTROL,
  effectiveEvictionReasonMultiSelect,
  evictionReasonRequired,
  GenerateTenantForm,
  resolveEvictionReasonField
} from '@ee/common/forms';
import { CustomFieldType, IntakeFormFields } from '@ee/common/enums';

@Component({
  selector: 'ee-eviction-tenants-section',
  template: `
    <div class="tenants-form section-wrapper" [ngClass]="{ 'demo-mode': demoMode }" [formGroup]="form">
      <div class="in-row-grid ee__standard-shadow">
        @if (tenants?.length) {
          <mat-accordion class="tenants-wrapper quad-column">
            @for (t of tenants.controls; track t; let i = $index) {
              <div class="tenants ee__standard-shadow">
                <mat-expansion-panel
                  [ngClass]="{ 'demo-mode': demoMode }"
                  [expanded]="i === tenants.controls.length - 1"
                  [disabled]="demoMode">
                  <mat-expansion-panel-header>
                    <mat-panel-title class="tenant-name">
                      @if (t.get('first_name')?.value && t.get('last_name')?.value) {
                        <span>{{ t.get('first_name')?.value }} {{ t.get('last_name')?.value }}</span>
                      } @else {
                        No name specified
                      }
                      @if (i > 0) {
                        <button
                          class="ee__button gray-bg black remove"
                          type="button"
                          mat-icon-button
                          aria-label="Remove"
                          (click)="removeTenant(i)"
                          tp="Remove tenant">
                          <mat-icon fontSet="fa" fontIcon="fa-trash"></mat-icon>
                        </button>
                      }
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="in-row-grid">
                    <mat-form-field class="double-column">
                      <mat-label>Tenant's First Name</mat-label>
                      <input matInput [formControl]="$any(t.get('first_name'))" required autocomplete="off" />
                      @if (t.get('first_name').hasError('required')) {
                        <mat-error>Please enter tenant's first name.</mat-error>
                      }
                      <mat-hint>Required. {{ tenantSectionLocalization?.['first_name_hint'] }}</mat-hint>
                    </mat-form-field>
                    @if (options[intakeFields.MIDDLE_NAME]?.show) {
                      <mat-form-field>
                        <mat-label>Tenant's Middle Name</mat-label>
                        <input matInput [formControl]="$any(t.get('middle_name'))" autocomplete="off" />
                        <mat-hint>Optional. {{ tenantSectionLocalization?.['middle_name_hint'] }}</mat-hint>
                      </mat-form-field>
                    }
                    <mat-form-field class="double-column">
                      <mat-label>Tenant's Last Name</mat-label>
                      <input matInput [formControl]="$any(t.get('last_name'))" required autocomplete="off" />
                      @if (t.get('last_name').hasError('required')) {
                        <mat-error>Please enter tenant's last name.</mat-error>
                      }
                      <mat-hint>Required. {{ tenantSectionLocalization?.['last_name_hint'] }}</mat-hint>
                    </mat-form-field>
                    @if (options[intakeFields.PHONE_NUMBER].show) {
                      <mat-form-field>
                        <mat-label>Phone Number</mat-label>
                        <input
                          matInput
                          mask="(000) 000-0000"
                          autocomplete="off"
                          [formControl]="$any(t.get('phone_number'))"
                          [required]="options[intakeFields.PHONE_NUMBER].required" />
                        @if (t.get('phone_number').hasError('required')) {
                          <mat-error>Please enter tenant's phone.</mat-error>
                        }
                        <mat-hint>
                          {{ options[intakeFields.PHONE_NUMBER].required ? 'Required.' : 'Optional.' }}
                          {{ tenantSectionLocalization?.['phone_number_hint'] }}
                        </mat-hint>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.SSN].show) {
                      <mat-form-field>
                        <mat-label>SSN</mat-label>
                        <input
                          matInput
                          mask="000-00-0000"
                          [formControl]="$any(t.get('ssn'))"
                          [required]="options[intakeFields.SSN].required"
                          autocomplete="off" />
                        @if (t.get('ssn').hasError('required')) {
                          <mat-error>Please enter tenant's social security number.</mat-error>
                        }
                        <mat-hint>
                          {{ options[intakeFields.SSN].required ? 'Required.' : 'Optional.' }}
                          {{ tenantSectionLocalization?.['ssn_enc_hint'] }}
                        </mat-hint>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.EMAIL]?.show) {
                      <mat-form-field>
                        <mat-label>Email Address</mat-label>
                        <input
                          matInput
                          [formControl]="$any(t.get('email'))"
                          [required]="options[intakeFields.EMAIL].required" />
                        @if (t.get('email').hasError('required')) {
                          <mat-error>Please enter tenant's email address.</mat-error>
                        }
                        <mat-hint>
                          {{ options[intakeFields.EMAIL]?.required ? 'Required.' : 'Optional.' }}
                          {{ tenantSectionLocalization?.['email_hint'] }}
                        </mat-hint>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.DOB]?.show) {
                      <mat-form-field>
                        <mat-label>Date of Birth</mat-label>
                        <input
                          matInput
                          [formControl]="$any(t.get('dob'))"
                          [matDatepicker]="dbDob"
                          [required]="options[intakeFields.DOB].required"
                          autocomplete="off" />
                        @if (t.get('dob').hasError('matDatepickerParse')) {
                          <mat-error>Enter a date as MM/DD/YYYY.</mat-error>
                        } @else if (t.get('dob').hasError('required')) {
                          <mat-error>Please enter tenant's date of birth.</mat-error>
                        }
                        <mat-hint>
                          {{ options[intakeFields.DOB]?.required ? 'Required.' : 'Optional.' }}
                          {{ tenantSectionLocalization?.['dob_hint'] }}
                        </mat-hint>
                        <mat-datepicker-toggle matSuffix [for]="dbDob"></mat-datepicker-toggle>
                        <mat-datepicker #dbDob></mat-datepicker>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.IS_NOT_ACTIVE_MILITARY_MEMBER]?.show) {
                      <div class="double-column">
                        <div class="flex flex-row justify-start items-center">
                          <mat-checkbox
                            class="mr-1"
                            [formControl]="$any(t.get('is_not_active_military_member'))"
                            [required]="
                              $safeNavigationMigration(options[intakeFields.IS_NOT_ACTIVE_MILITARY_MEMBER]?.required)
                            ">
                            Tenant is not an active military member.
                          </mat-checkbox>
                          <a href="https://scra.dmdc.osd.mil/scra/" target="_blank" class="pt-2">
                            <mat-icon
                              fontSet="fa"
                              fontIcon="fa-info-circle"
                              tp="Click to see more information about SCRA laws."></mat-icon>
                          </a>
                        </div>
                        @if (t.get('is_not_active_military_member').hasError('requiredTrue')) {
                          <mat-error>Tenant cannot be an active member of the military to proceed.</mat-error>
                        }
                      </div>
                    }
                  </div>
                </mat-expansion-panel>
              </div>
            }
          </mat-accordion>
        } @else {
          <div class="invalid">Please add one or more tenants to continue.</div>
        }
        @if (!demoMode) {
          <button
            class="quad-column"
            mat-flat-button
            type="button"
            color="primary"
            [disabled]="form.invalid"
            (click)="addTenant()">
            Add Another Tenant
          </button>
        }
      </div>

      <!-- A visible divider between the tenants and the case-level questions below. It used to be an
           invisible hr with 16px above and below, which read as an unexplained gap. -->
      <hr class="section-divider" />

      <!-- Only rendered when its checkbox is shown: an empty grid row still adds its padding and gap,
           which stacked into dead space above the eviction reason. The rental-assistance checkbox that
           shared this row was retired (EE-392 Req 12); stored values still show on the case summary. -->
      @if (options[intakeFields.NOT_ACTIVE_MILITARY]?.show) {
        <div class="in-row-grid">
          <div class="double-column">
            <div class="flex flex-row justify-start items-center">
              <mat-checkbox
                class="mr-1"
                formControlName="not_active_military"
                [required]="$safeNavigationMigration(options[intakeFields.NOT_ACTIVE_MILITARY]?.required)">
                Tenant(s) are not active military members.
              </mat-checkbox>
              <a href="https://scra.dmdc.osd.mil/scra/" target="_blank">
                <mat-icon
                  fontSet="fa"
                  fontIcon="fa-info-circle"
                  tp="Click to see more information about SCRA laws."></mat-icon>
              </a>
            </div>
            @if (form.get('not_active_military').hasError('required')) {
              <mat-error>Tenant(s) cannot be active member of the military to proceed.</mat-error>
            }
          </div>
        </div>
      }

      <div class="in-row-grid">
        @if (options?.[intakeFields.EVICTION_REASON]?.show) {
          <!-- The server-generated Reason_Field, rendered like any custom control: a Radio Group bound to
               eviction_reason, or checkboxes bound to the eviction_reasons list (Req 4.1). ee-field also
               union-renders a stored reason the firm no longer offers, so none is dropped (Req 4.6). -->
          <ee-field
            class="double-column"
            [field]="renderedReasonField"
            [control]="$any(reasonControl)"
            [compact]="false"></ee-field>
        }
        @if (options?.[intakeFields.REASON]?.show) {
          <mat-form-field class="triple-column">
            <mat-label>Other Reason Description</mat-label>
            <input matInput formControlName="reason" [required]="options[intakeFields.REASON].required" />
            @if (form.get('reason').hasError('required')) {
              <mat-error>An eviction reason is required.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.REASON].required ? 'Required.' : 'Optional.' }}
              {{ tenantSectionLocalization?.['reason_hint'] }}
            </mat-hint>
          </mat-form-field>
        }
      </div>

      @if (tenantSection?.fields?.length && tenantFields) {
        <div class="in-row-grid" formGroupName="custom_fields">
          @for (f of tenantSection?.fields; track f; let i = $index) {
            <ee-field
              [ngClass]="{
                'margin-bottom-sm': i < tenantSection.fields.length - 1,
                'md:mb-4': i < tenantSection.fields.length - 1,
                'double-column': f.columns === 2,
                'triple-column': f.columns === 3,
                'quad-column': f.columns === 4
              }"
              [hideFromIntake]="f.hide_from_intake"
              [hidden]="f.hide_from_intake && !overrideFieldVisibility"
              [control]="$any(tenantFields.controls[f.field])"
              [compact]="false"
              [field]="f"></ee-field>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .tenant-name {
        font-size: 1.1rem;
        justify-content: space-between;
        align-items: center;
      }

      .tenants-form {
        &.demo-mode {
          .tenants {
            padding-right: 0;
          }
        }
      }
      .invalid {
        color: red;
      }
      .section-divider {
        margin: 8px 0;
        border: none;
        border-top: 1px solid var(--color-outline, rgba(0, 0, 0, 0.12));
      }
      mat-mdc-accordion.tenants-wrapper {
        .mat-mdc-expansion-panel-header-title {
          font-size: 1rem;
          font-weight: 500;
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EvictionTenantsSectionComponent implements OnInit {
  @Input() public form: UntypedFormGroup;

  @Input() public options: Map<string, IntakeOption> | undefined;

  @Input() public demoMode = false;

  @Input() public tenantSectionLocalization: { [key: string]: string | undefined } = {};

  @Input() overrideFieldVisibility = false;

  @Input({ required: true }) public tenantSection: IntakeFormSection;

  /**
   * The server-generated Reason_Field (`evictionReasonFieldOf(intakeForm)`, Req 4.2). Hosts that only have
   * the options map (the builder/customizer previews) leave it unset, and it is derived from `options`.
   */
  @Input() public reasonField: CustomField | undefined;

  /**
   * Forces checkbox rendering regardless of Display_Mode (Req 4.7): set when the form starts from a stored
   * case holding more than one reason — the edit-details dialog's case or a cloned case
   * (`storedCaseNeedsCheckboxes`) — so a control bound to one value cannot silently drop or hide the rest.
   * A fresh intake never sets it and so always follows the firm's own mode.
   */
  @Input() public forceMultiSelectReasons = false;

  intakeFields = IntakeFormFields;

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit() {
    if (!this.tenants.length) {
      this.addTenant();
    }
  }

  /** Checkboxes for MULTI_SELECT (or when forced), otherwise a Radio Group (Req 4.1, 4.7). */
  public get multiSelectReasons(): boolean {
    return effectiveEvictionReasonMultiSelect(this.resolvedReasonField, this.forceMultiSelectReasons);
  }

  /** The control the rendered group writes: the list for checkboxes, the scalar for a Radio Group. */
  public get reasonControl(): AbstractControl | null {
    return (
      this.form?.get(this.multiSelectReasons ? EVICTION_REASONS_CONTROL : IntakeFormFields.EVICTION_REASON) ?? null
    );
  }

  /**
   * The Reason_Field as rendered: typed by the effective mode, with the requiredness the form's validator
   * uses and the section's localized hint. Rebuilt only when what it shows changes: ee-field rebuilds its
   * option list on every new `field` object, so a getter returning a fresh object would do so each tick.
   * No default here — defaults are the form builder's business, on a new intake only.
   */
  public get renderedReasonField(): CustomField {
    const source = this.resolvedReasonField;
    const type = this.multiSelectReasons ? CustomFieldType.MULTI_SELECT : CustomFieldType.RADIO;
    const required = evictionReasonRequired(this.options);
    const hint = this.tenantSectionLocalization?.['eviction_reason_hint'] ?? source.hint;
    const key = JSON.stringify([source, type, required, hint]);
    if (key !== this.renderedReasonFieldKey) {
      this.renderedReasonFieldKey = key;
      this.cachedRenderedReasonField = { ...source, type, required, hint };
    }
    return this.cachedRenderedReasonField;
  }

  private get resolvedReasonField(): CustomField {
    return resolveEvictionReasonField(this.reasonField, this.options);
  }

  private renderedReasonFieldKey: string | null = null;

  private cachedRenderedReasonField: CustomField;

  get tenantFields(): UntypedFormGroup | undefined {
    return this.form.get('custom_fields') as UntypedFormGroup;
  }

  addTenant() {
    const newTenant = GenerateTenantForm(this.fb, this.options, this.demoMode);
    if (this.demoMode) {
      newTenant.get('first_name').setValue('Demo');
      newTenant.get('last_name').setValue('Tenant');
    }
    this.tenants.push(newTenant, { emitEvent: false });
  }

  removeTenant(i: number) {
    this.tenants.removeAt(i);
  }

  get tenants(): UntypedFormArray {
    return this.form.get('tenants') as UntypedFormArray;
  }
}
